/**
 * SermonPage — one sermon's own shareable page at /media/:slug.
 * Player, description, scripture, speaker, series, notes/transcript, a give
 * link and what to listen to next.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft, ArrowRight, BookOpen, Heart, Headphones, Play, Share2, UserRound,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const embed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

export default function SermonPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [showNotes, setShowNotes] = useState(false);

  const { data: sermon, isLoading, error } = useQuery({
    queryKey: ['public-sermon', slug],
    queryFn: () => fetch(`${API_URL}/api/fwtv/public/sermons/${slug}`).then((r) => {
      if (!r.ok) throw new Error('Sermon not found');
      return r.json();
    }),
    retry: false,
  });

  const { data: transcript } = useQuery({
    queryKey: ['public-sermon-transcript', slug],
    queryFn: () => fetch(`${API_URL}/api/fwtv/public/sermons/${slug}/transcript`)
      .then((r) => (r.ok ? r.json() : null)).catch(() => null),
    enabled: !!sermon?.has_transcript && showNotes,
  });

  useEffect(() => {
    if (sermon?.title) document.title = sermon.title;
  }, [sermon?.title]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: sermon?.title, url }); return; } catch { /* cancelled */ }
    }
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  if (isLoading) return <div className="p-10"><Skeleton className="h-72 w-full" /></div>;
  if (error || !sermon) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] grid place-items-center text-center px-6"
           data-testid="sermon-not-found">
        <div>
          <p className="text-black/60">We could not find that message.</p>
          <Button className="mt-6 rounded-none" onClick={() => navigate('/media')}>
            Back to the library
          </Button>
        </div>
      </div>
    );
  }

  const iframeSrc = embed(sermon.video_url);
  const refs = sermon.scripture_refs || sermon.scriptures || [];
  const give = sermon.donation_campaign
    ? `/campaigns/${sermon.donation_campaign.slug || sermon.donation_campaign.id}`
    : '/giving';

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#14110F]" data-testid="sermon-page">
      <header className="border-b border-black/[0.08]">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10 py-6 flex items-center justify-between gap-4">
          <Link to="/media" className="text-xs text-black/50 hover:text-black inline-flex items-center gap-1.5"
                data-testid="sermon-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All teaching
          </Link>
          <Button size="sm" variant="outline" onClick={share} data-testid="sermon-share-btn"
                  className="rounded-none border-black/15 bg-transparent hover:bg-black/[0.03]">
            <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 lg:px-10 py-14">
        <p className="text-[11px] font-mono text-black/40" data-testid="sermon-date">
          {sermon.publication_date
            ? new Date(sermon.publication_date).toLocaleDateString(undefined,
                { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
          {sermon.duration_seconds ? ` · ${Math.round(sermon.duration_seconds / 60)} min` : ''}
        </p>
        <h1 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Newsreader', Georgia, serif" }} data-testid="sermon-title">
          {sermon.title}
        </h1>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {sermon.speaker && (
            <span className="inline-flex items-center gap-2 text-sm text-black/60"
                  data-testid="sermon-speaker">
              <UserRound className="h-3.5 w-3.5" /> {sermon.speaker.name}
            </span>
          )}
          {sermon.series && (
            <Badge variant="outline" className="rounded-none border-black/15 text-[11px]"
                   data-testid="sermon-series">{sermon.series.name}</Badge>
          )}
          {(Array.isArray(refs) ? refs : [refs]).filter(Boolean).map((r) => (
            <Badge key={r} variant="outline"
                   className="rounded-none border-[#B4881F]/40 text-[#B4881F] text-[11px]"
                   data-testid={`sermon-scripture-${r}`}>
              <BookOpen className="h-3 w-3 mr-1" /> {r}
            </Badge>
          ))}
        </div>

        {iframeSrc ? (
          <div className="mt-10 aspect-video bg-black" data-testid="sermon-video">
            <iframe src={iframeSrc} title={sermon.title} allowFullScreen
                    className="w-full h-full" frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" />
          </div>
        ) : sermon.video_url ? (
          <video controls src={sermon.video_url} data-testid="sermon-video"
                 className="mt-10 w-full aspect-video bg-black" />
        ) : null}

        {sermon.audio_url && (
          <div className="mt-6" data-testid="sermon-audio">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-black/40 mb-3">
              Listen
            </p>
            <audio controls src={sermon.audio_url} className="w-full" />
          </div>
        )}

        {sermon.description && (
          <p className="mt-10 text-lg text-black/60 leading-relaxed whitespace-pre-line"
             data-testid="sermon-description">{sermon.description}</p>
        )}

        {(sermon.notes || sermon.has_transcript) && (
          <div className="mt-10 border-t border-black/[0.08] pt-8">
            <Button variant="outline" onClick={() => setShowNotes((v) => !v)}
                    data-testid="sermon-notes-toggle"
                    className="rounded-none border-black/15 bg-transparent hover:bg-black/[0.03]">
              {showNotes ? 'Hide notes' : 'Sermon notes & transcript'}
            </Button>
            {showNotes && (
              <div className="mt-6 space-y-6" data-testid="sermon-notes">
                {sermon.notes && (
                  <p className="text-black/60 leading-relaxed whitespace-pre-line">{sermon.notes}</p>
                )}
                {sermon.has_transcript && (
                  <p className="text-sm text-black/55 leading-relaxed whitespace-pre-line">
                    {transcript?.text || (transcript?.segments || [])
                      .map((s) => s.text).join(' ') || 'Preparing the transcript…'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-12 flex flex-wrap gap-3">
          <Button onClick={() => navigate(give)} data-testid="sermon-give-btn"
                  className="rounded-none bg-[#B4881F] text-black hover:brightness-110">
            <Heart className="h-4 w-4 mr-2" />
            {sermon.donation_campaign ? `Give to ${sermon.donation_campaign.name}` : 'Give'}
          </Button>
          {sermon.audio_url && (
            <a href={sermon.audio_url} target="_blank" rel="noreferrer">
              <Button variant="outline"
                      className="rounded-none border-black/15 bg-transparent hover:bg-black/[0.03]">
                <Headphones className="h-4 w-4 mr-2" /> Download audio
              </Button>
            </a>
          )}
        </div>

        {(sermon.related || []).length > 0 && (
          <div className="mt-16 border-t border-black/[0.08] pt-10" data-testid="sermon-related">
            <h2 className="text-xl font-semibold" style={{ fontFamily: "'Newsreader', Georgia, serif" }}>
              {sermon.series ? `More from ${sermon.series.name}` : 'Listen next'}
            </h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {sermon.related.map((r) => (
                <button key={r.id} onClick={() => navigate(`/media/${r.slug || r.id}`)}
                        data-testid={`sermon-related-${r.id}`}
                        className="group text-left bg-white border border-black/[0.08] p-6
                                   hover:border-black/20 transition-colors">
                  <Play className="h-4 w-4 text-[#B4881F]" />
                  <p className="mt-4 font-semibold leading-snug"
                     style={{ fontFamily: "'Newsreader', Georgia, serif" }}>{r.title}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#B4881F]">
                    Open <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
